import { useEffect, useState } from "react";
import { CheckCircle2, Trash2, MessageSquare } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";

export default function Feedback() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all");

  const load = () => api.get("/admin/feedback").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const resolve = async (id) => {
    try {
      await api.post(`/admin/feedback/${id}/resolve`);
      toast.success("Marked resolved");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this feedback?")) return;
    try {
      await api.delete(`/admin/feedback/${id}`);
      toast.success("Feedback removed");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const filtered = items.filter((f) => filter === "all" || f.status === filter);
  const openCount = items.filter((f) => f.status === "open").length;

  return (
    <div data-testid="admin-feedback">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <h1 className="font-heading text-3xl font-black uppercase tracking-tight">
          Feedback <span className="text-[#D4AF37]">({openCount} open)</span>
        </h1>
        <div className="flex gap-2">
          {["all", "open", "resolved"].map((f) => (
            <button key={f} onClick={() => setFilter(f)}
              className={`text-xs uppercase tracking-widest px-4 py-2 border transition-colors duration-200 ${
                filter === f ? "bg-[#D4AF37] text-black border-[#D4AF37]" : "border-white/15 text-white/60 hover:border-[#D4AF37]"
              }`}
              data-testid={`feedback-filter-${f}`}>
              {f}
            </button>
          ))}
        </div>
      </div>

      {filtered.length === 0 && (
        <div className="border border-white/10 bg-[#0A0A0A] p-10 text-center" data-testid="feedback-empty">
          <MessageSquare size={32} className="text-white/20 mx-auto mb-3" />
          <p className="text-white/40 text-sm">No feedback in this view yet.</p>
        </div>
      )}

      <div className="space-y-3">
        {filtered.map((f) => (
          <div key={f.id} className={`border p-5 ${f.status === "resolved" ? "border-white/5 bg-[#0A0A0A] opacity-60" : "border-white/10 bg-[#0A0A0A]"}`} data-testid={`feedback-${f.id}`}>
            <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-3 flex-wrap">
                  <span className="text-[10px] uppercase tracking-widest px-2 py-1 border border-[#D4AF37]/40 text-[#D4AF37]">{f.category}</span>
                  <span className={`text-[10px] uppercase tracking-widest px-2 py-1 border ${f.status === "resolved" ? "border-white/20 text-white/40" : "border-orange-400/40 text-orange-300"}`}>{f.status}</span>
                  <span className="text-white/40 text-xs">{new Date(f.created_at).toLocaleString("en-IN")}</span>
                </div>
                <p className="text-sm mt-2 font-semibold">
                  {f.submitted_by_name || f.submitted_by}
                  {f.player_name && <span className="text-white/50 font-normal"> · {f.player_name}</span>}
                  <span className="text-white/40 font-normal ml-2 text-xs">({f.role})</span>
                </p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {f.status === "open" && (
                  <button onClick={() => resolve(f.id)} className="flex items-center gap-1.5 border border-[#D4AF37] text-[#D4AF37] text-[10px] uppercase tracking-widest px-3 py-1.5 hover:bg-[#D4AF37] hover:text-black transition-colors duration-200" data-testid={`feedback-resolve-${f.id}`}>
                    <CheckCircle2 size={12} /> Resolve
                  </button>
                )}
                <button onClick={() => remove(f.id)} className="text-red-400/70 hover:text-red-400 p-1.5" data-testid={`feedback-delete-${f.id}`}>
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
            <p className="text-white/70 text-sm leading-relaxed whitespace-pre-wrap border-t border-white/5 pt-3">{f.message}</p>
          </div>
        ))}
      </div>
    </div>
  );
}
